/**
 * Pricing-v2 recurring-interval helpers for catalog Prices.
 *
 * A subscription Price carries a `recurring` block — `interval` (the
 * unit) × `intervalCount` (how many units per billing cycle). The API
 * rejects a one-time Price (no `recurring`) from the catalog, so the
 * create-price dialog always sends one.
 *
 * Mirrors the API `RecurringDto` (`admin-catalog.dto.ts`): `intervalCount`
 * is 1–366 (Stripe caps the total span at one year).
 *
 * Pure + side-effect free — unit-tested in `price-interval.test.ts`.
 */

export const PRICE_INTERVAL_UNITS = ["day", "week", "month", "year"] as const;
export type PriceIntervalUnit = (typeof PRICE_INTERVAL_UNITS)[number];

export const PRICE_INTERVAL_COUNT_MIN = 1;
export const PRICE_INTERVAL_COUNT_MAX = 366;

const UNIT_SET: ReadonlySet<string> = new Set(PRICE_INTERVAL_UNITS);

export function isPriceIntervalUnit(value: string): value is PriceIntervalUnit {
  return UNIT_SET.has(value);
}

/**
 * Strict positive-integer parse of the interval-count input. Rejects
 * floats, signs, blank, and out-of-range values; returns `null` on any
 * of them so the dialog can block submit.
 */
export function parseIntervalCount(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(n) || n < PRICE_INTERVAL_COUNT_MIN || n > PRICE_INTERVAL_COUNT_MAX) {
    return null;
  }
  return n;
}

export function isIntervalCountValid(raw: string): boolean {
  return parseIntervalCount(raw) !== null;
}
